<template>
    <div>
        <!-- Admin actions (District/State admin) -->
        <div class="section pt-1" v-if="isCenterAdmin">
            <div class="row mt-1 align-items-center">
                <div class="col-9">
                    <button type="button" class="btn btn-primary btn-block add-btn" @click="openAddCenterModal">
                        <ion-icon name="add-outline"></ion-icon>
                        <span class="mal-text">കേന്ദ്രം ചേർക്കുക</span>
                        <ion-icon class="center-icon" name="location-outline"></ion-icon>
                    </button>
                </div>
                <div class="col-3 text-right">
                    <button type="button" class="btn btn-light kebab-btn" @click="openAssignCentersModal" aria-label="Open actions">
                        <!-- Using a text glyph ensures visibility even if ionicons fail to render -->
                        <span class="kebab-fallback" aria-hidden="true">⋮</span>
                    </button>
                </div>
            </div>

            <div class="row mt-2" v-if="isStateAdmin">
                <div class="col-12">
                    <label class="label mal-text" for="pdfDistrict">District</label>
                    <select id="pdfDistrict" class="form-control" v-model="selectedDistrictIdForPdf" :disabled="districtsLoading">
                        <option value="" disabled class="mal-text">{{ districtsLoading ? 'Loading...' : 'Select District' }}</option>
                        <option v-for="d in districts" :key="'pdf-d-'+d.id" :value="String(d.id)">{{ d.name }}</option>
                    </select>
                </div>
            </div>

            <div class="row mt-2">
                <div class="col-12">
                    <button type="button" class="btn btn-outline-secondary btn-block" :disabled="pdfDownloading" @click="downloadCentersCandidatesPdf">
                        <span class="mal-text">{{ pdfDownloading ? 'Downloading...' : 'Export PDF (Centers + Candidates)' }}</span>
                    </button>
                </div>
            </div>

            <div class="row mt-2" v-if="selectedCandidateIds.length">
                <div class="col-12">
                    <button type="button" class="btn btn-outline-primary btn-block" @click="openAssignCandidateCenterModal">
                        <span class="mal-text">Selected Candidates ന് Center Assign ചെയ്യുക</span>
                        <span class="ml-1">({{ selectedCandidateIds.length }})</span>
                    </button>
                </div>
            </div>
        </div>

        <!-- Total summary -->
        <div class="section pt-1">
            <div class="row mt-1">
                <div class="col-12">
                    <div class="stat-box">
                        <div class="title mal-text">QVP പരീക്ഷ ഫലം (Total)</div>
                        <div class="value text-primary">{{ count_total }}</div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Exam filter -->
        <div class="section" v-if="exams && exams.length">
            <div class="row mt-1">
                <div class="col-12">
                    <label class="label mal-text" for="exam">പരീക്ഷ</label>
                    <select id="exam" class="form-control" v-model="exam_id" @change="fetchReport">
                        <option value="">എല്ലാം</option>
                        <option v-for="exam in exams" :key="exam.id" :value="exam.id">
                            {{ exam.name || ('Exam ' + exam.id) }}
                        </option>
                    </select>
                </div>
            </div>
        </div>

        <!-- Title -->
        <div class="section pt-0 mt-0">
            <div class="listed-detail mt-3">
                <h3 class="text-center mt-2 mal-text custom-title">{{ page_title }}</h3>
            </div>
        </div>

        <!-- Loading -->
        <div class="section text-center" v-if="loading_list">
            <div class="spinner-border text-secondary" role="status"></div>
        </div>

        <!-- Empty state -->
        <div class="listed-detail mt-0">
            <h4 class="text-center mt-0 mal-text" v-if="!loading_list && (!list || list.length === 0)">
                ഡാറ്റ ഇല്ല
            </h4>
        </div>

        <!-- Table: Name, Mark, Phone Number (sorted high to low) -->
        <div class="section mt-2 mb-2" v-if="!loading_list && list && list.length > 0">
            <div class="card">
                <div class="table-responsive">
                    <table class="table">
                        <thead>
                            <tr>
                                <th scope="col">#</th>
                                <th scope="col" v-if="isCenterAdmin" class="text-center">✓</th>
                                <th scope="col" class="mal-text">പേര്</th>
                                <th scope="col" class="mal-text text-right">മാർക്ക്</th>
                                <th scope="col" class="mal-text">കേന്ദ്രം</th>
                                <th scope="col" class="mal-text">ഫോൺ നമ്പർ</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(item, index) in sortedList" :key="item.id || index">
                                <td class="text-primary font-dark">{{ index + 1 }}</td>
                                <td v-if="isCenterAdmin" class="text-center">
                                    <input
                                        v-if="isQualified(item)"
                                        type="checkbox"
                                        :value="item.id"
                                        v-model="selectedCandidateIds"
                                        aria-label="Select candidate"
                                    />
                                </td>
                                <td class="text-primary font-dark mal-text">
                                    {{ item.name }}
                                    <span v-if="item.location_name" class="d-block text-muted small mal-text">
                                        ({{ item.location_name }})
                                    </span>
                                </td>
                                <td class="text-primary font-dark text-right">
                                    {{ getTotalMark(item) }}
                                </td>
                                <td class="text-primary font-dark mal-text">
                                    <span v-if="item.assigned_center_name">{{ item.assigned_center_name }}</span>
                                    <span v-else class="text-muted">-</span>
                                    <button
                                        v-if="isCenterAdmin && isQualified(item)"
                                        type="button"
                                        class="btn btn-sm btn-outline-primary ml-2"
                                        @click="openAssignForSingle(item)"
                                    >
                                        {{ item.assigned_center_name ? 'Reassign' : 'Assign' }}
                                    </button>
                                </td>
                                <td class="text-primary font-dark">
                                    {{ getNumber(item) }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <!-- Add Center Modal (bottom sheet) -->
        <div v-if="addCenterModalOpen" class="modal-overlay action-sheet-open" @click.self="closeAddCenterModal">
            <div class="modal-sheet">
                <div class="modal-sheet-handle"></div>
                <h5 class="modal-title mal-text">കേന്ദ്രം ചേർക്കുക (Add Center)</h5>
                <form @submit.prevent="submitAddCenter">
                    <div class="form-group" v-if="isStateAdmin">
                        <label class="form-label mal-text">District <span class="text-danger">*</span></label>
                        <select v-model="centerForm.district_id" class="form-control" required @change="onDistrictChangeForCenterForm">
                            <option value="" disabled class="mal-text">{{ districtsLoading ? 'Loading...' : 'Select District' }}</option>
                            <option v-for="d in districts" :key="d.id" :value="d.id">{{ d.name }}</option>
                        </select>
                    </div>

                    <div class="form-group">
                        <label class="form-label mal-text">Center Name <span class="text-danger">*</span></label>
                        <input v-model="centerForm.center_name" type="text" class="form-control" placeholder="Center name" required />
                    </div>

                    <div class="form-group">
                        <label class="form-label mal-text">Zone <span class="text-danger">*</span></label>
                        <select v-model="centerForm.zone_id" class="form-control" required @change="onZoneChange">
                            <option value="" disabled class="mal-text">Select Zone</option>
                            <option v-for="z in zones" :key="z.id" :value="z.id">{{ z.name }}</option>
                        </select>
                    </div>

                    <div class="form-group">
                        <label class="form-label mal-text">Unit <span class="text-danger">*</span></label>
                        <select v-model="centerForm.unit_id" class="form-control" required :disabled="!centerForm.zone_id || unitsLoading">
                            <option value="" disabled class="mal-text">
                                {{ unitsLoading ? 'Loading...' : 'Select Unit' }}
                            </option>
                            <option v-for="u in units" :key="u.id" :value="u.id">{{ u.name }}</option>
                        </select>
                    </div>

                    <div class="form-group">
                        <label class="form-label mal-text">Convenor Name <span class="text-danger">*</span></label>
                        <input v-model="centerForm.convenor_name" type="text" class="form-control" placeholder="Convenor name" required />
                    </div>

                    <div class="form-group">
                        <label class="form-label mal-text">Phone Number <span class="text-danger">*</span></label>
                        <input v-model="centerForm.convenor_phone" type="tel" class="form-control" placeholder="10 digit mobile number" maxlength="10"
                            inputmode="numeric" required />
                    </div>

                    <div class="form-group">
                        <label class="form-label mal-text">Exam Time (From) <span class="text-danger">*</span></label>
                        <input v-model="centerForm.exam_time_from" type="time" class="form-control" required />
                    </div>

                    <div class="form-group">
                        <label class="form-label mal-text">Exam Time (To) <span class="text-danger">*</span></label>
                        <input v-model="centerForm.exam_time_to" type="time" class="form-control" required />
                    </div>

                    <div class="modal-actions">
                        <button type="button" class="btn btn-secondary" @click="closeAddCenterModal">ക്യാൻസൽ</button>
                        <button type="submit" class="btn btn-primary" :disabled="centerSaving">
                            {{ centerSaving ? 'സേവ് ചെയ്യുന്നു...' : 'സേവ് ചെയ്യുക' }}
                        </button>
                    </div>
                </form>
            </div>
        </div>

        <!-- Assign Centers Modal (bottom sheet) -->
        <div v-if="assignCentersModalOpen" class="modal-overlay action-sheet-open" @click.self="closeAssignCentersModal">
            <div class="modal-sheet">
                <div class="modal-sheet-handle"></div>
                <h5 class="modal-title mal-text">കേന്ദ്രങ്ങൾ അസൈൻ ചെയ്യുക (Assign Centers)</h5>

                <div class="form-group" v-if="isStateAdmin">
                    <label class="form-label mal-text">District <span class="text-danger">*</span></label>
                    <select v-model="selectedDistrictIdForCenters" class="form-control" required @change="onDistrictChangeForCenters">
                        <option value="" disabled class="mal-text">{{ districtsLoading ? 'Loading...' : 'Select District' }}</option>
                        <option v-for="d in districts" :key="d.id" :value="d.id">{{ d.name }}</option>
                    </select>
                </div>

                <div v-if="centersLoading" class="text-center py-3">
                    <div class="spinner-border text-primary" role="status"></div>
                </div>

                <div v-else>
                    <div v-if="centers.length === 0" class="text-muted mal-text text-center py-2">
                        കേന്ദ്രങ്ങൾ ഇല്ല
                    </div>

                    <div v-else class="centers-checklist">
                        <div v-for="c in centers" :key="c.id" class="center-check-item d-flex align-items-start justify-content-between">
                            <label class="m-0 flex-grow-1" style="cursor:pointer;">
                                <input type="checkbox" :value="c.id" v-model="selectedCenterIds" />
                                <span class="mal-text">{{ c.center_name || c.name || ('Center #' + c.id) }}</span>
                                <small class="text-muted d-block" v-if="c.zone_name || c.unit_name">
                                    {{ c.zone_name ? ('Zone: ' + c.zone_name) : '' }}{{ c.zone_name && c.unit_name ? ' | ' : '' }}{{ c.unit_name ? ('Unit: ' + c.unit_name) : '' }}
                                </small>
                            </label>
                            <div class="ml-2 d-flex flex-column gap-1">
                                <button
                                    type="button"
                                    class="btn btn-sm btn-outline-primary"
                                    @click="openEditCenterModal(c)"
                                    aria-label="Edit center"
                                >
                                    Edit
                                </button>
                                <button
                                    type="button"
                                    class="btn btn-sm btn-outline-danger"
                                    :disabled="deleteCenterLoadingId === c.id"
                                    @click="confirmDeleteCenter(c)"
                                    aria-label="Delete center"
                                >
                                    {{ deleteCenterLoadingId === c.id ? '...' : 'Delete' }}
                                </button>
                            </div>
                        </div>
                    </div>

                    <div class="modal-actions">
                        <button type="button" class="btn btn-secondary" @click="closeAssignCentersModal">ക്യാൻസൽ</button>
                        <button type="button" class="btn btn-primary" :disabled="assignSaving" @click="submitAssignCenters">
                            {{ assignSaving ? 'സേവ് ചെയ്യുന്നു...' : 'സേവ് ചെയ്യുക' }}
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Edit Center Modal (bottom sheet) -->
        <div v-if="editCenterModalOpen" class="modal-overlay action-sheet-open" @click.self="closeEditCenterModal">
            <div class="modal-sheet">
                <div class="modal-sheet-handle"></div>
                <h5 class="modal-title mal-text">കേന്ദ്രം എഡിറ്റ് ചെയ്യുക (Edit Center)</h5>
                <form @submit.prevent="submitEditCenter">
                    <div class="form-group" v-if="isStateAdmin">
                        <label class="form-label mal-text">District <span class="text-danger">*</span></label>
                        <select v-model="editCenterForm.district_id" class="form-control" required @change="onDistrictChangeForEditForm">
                            <option value="" disabled class="mal-text">{{ districtsLoading ? 'Loading...' : 'Select District' }}</option>
                            <option v-for="d in districts" :key="'ed-d-'+d.id" :value="String(d.id)">{{ d.name }}</option>
                        </select>
                    </div>

                    <div class="form-group">
                        <label class="form-label mal-text">Center Name <span class="text-danger">*</span></label>
                        <input v-model="editCenterForm.center_name" type="text" class="form-control" placeholder="Center name" required />
                    </div>

                    <div class="form-group">
                        <label class="form-label mal-text">Zone <span class="text-danger">*</span></label>
                        <select v-model="editCenterForm.zone_id" class="form-control" required @change="onZoneChangeForEditForm">
                            <option value="" disabled class="mal-text">Select Zone</option>
                            <option v-for="z in editZones" :key="'ed-z-'+z.id" :value="String(z.id)">{{ z.name }}</option>
                        </select>
                    </div>

                    <div class="form-group">
                        <label class="form-label mal-text">Unit <span class="text-danger">*</span></label>
                        <select v-model="editCenterForm.unit_id" class="form-control" required :disabled="!editCenterForm.zone_id || editUnitsLoading">
                            <option value="" disabled class="mal-text">
                                {{ editUnitsLoading ? 'Loading...' : 'Select Unit' }}
                            </option>
                            <option v-for="u in editUnits" :key="'ed-u-'+u.id" :value="String(u.id)">{{ u.name }}</option>
                        </select>
                    </div>

                    <div class="form-group">
                        <label class="form-label mal-text">Convenor Name <span class="text-danger">*</span></label>
                        <input v-model="editCenterForm.convenor_name" type="text" class="form-control" placeholder="Convenor name" required />
                    </div>

                    <div class="form-group">
                        <label class="form-label mal-text">Phone Number <span class="text-danger">*</span></label>
                        <input v-model="editCenterForm.convenor_phone" type="tel" class="form-control" placeholder="10 digit mobile number" maxlength="10"
                            inputmode="numeric" required />
                    </div>

                    <div class="form-group">
                        <label class="form-label mal-text">Exam Time (From) <span class="text-danger">*</span></label>
                        <input v-model="editCenterForm.exam_time_from" type="time" class="form-control" required />
                    </div>

                    <div class="form-group">
                        <label class="form-label mal-text">Exam Time (To) <span class="text-danger">*</span></label>
                        <input v-model="editCenterForm.exam_time_to" type="time" class="form-control" required />
                    </div>

                    <div class="modal-actions">
                        <button type="button" class="btn btn-secondary" @click="closeEditCenterModal">ക്യാൻസൽ</button>
                        <button type="submit" class="btn btn-primary" :disabled="editCenterSaving">
                            {{ editCenterSaving ? 'സേവ് ചെയ്യുന്നു...' : 'സേവ് ചെയ്യുക' }}
                        </button>
                    </div>
                </form>
            </div>
        </div>

        <!-- Assign Center to Candidates (bottom sheet) -->
        <div v-if="assignCandidateCenterModalOpen" class="modal-overlay action-sheet-open" @click.self="closeAssignCandidateCenterModal">
            <div class="modal-sheet">
                <div class="modal-sheet-handle"></div>
                <h5 class="modal-title mal-text">കേന്ദ്രം അസൈൻ ചെയ്യുക</h5>

                <div v-if="assignCandidateFlash.message" class="assign-flash" :class="assignCandidateFlash.type === 'success' ? 'flash-success' : 'flash-error'">
                    <div class="mal-text">{{ assignCandidateFlash.message }}</div>
                </div>

                <div v-if="selectedCandidateIds.length === 1 && prefillCenterNameForCandidates" class="form-group">
                    <label class="form-label mal-text">Current Center</label>
                    <div class="text-muted mal-text">{{ prefillCenterNameForCandidates }}</div>
                </div>

                <div class="form-group">
                    <label class="form-label mal-text">Selected Candidates</label>
                    <div class="text-muted mal-text">{{ selectedCandidateIds.length }} selected</div>
                </div>

                <div class="form-group" v-if="isStateAdmin">
                    <label class="form-label mal-text">District <span class="text-danger">*</span></label>
                    <select v-model="selectedDistrictIdForCandidateAssignment" class="form-control" required @change="onDistrictChangeForCandidateAssignment">
                        <option value="" disabled class="mal-text">{{ districtsLoading ? 'Loading...' : 'Select District' }}</option>
                        <option v-for="d in districts" :key="d.id" :value="d.id">{{ d.name }}</option>
                    </select>
                </div>

                <div class="form-group">
                    <label class="form-label mal-text">Center <span class="text-danger">*</span></label>
                    <select v-model="selectedCenterIdForCandidates" class="form-control" required :disabled="centersLoading">
                        <option value="" disabled class="mal-text">{{ centersLoading ? 'Loading...' : 'Select Center' }}</option>
                        <option v-for="c in centersForCandidateAssignment" :key="c.id" :value="c.id">
                            {{ c.center_name || ('Center #' + c.id) }}
                        </option>
                    </select>
                </div>

                <div class="modal-actions">
                    <button type="button" class="btn btn-secondary" @click="closeAssignCandidateCenterModal">ക്യാൻസൽ</button>
                    <button
                        type="button"
                        class="btn btn-outline-danger"
                        :disabled="unassignCandidateSaving || assignCandidateSaving"
                        @click="submitUnassignCenterForCandidates"
                    >
                        {{ unassignCandidateSaving ? 'Clearing...' : 'Clear Assignment' }}
                    </button>
                    <button type="button" class="btn btn-primary" :disabled="assignCandidateSaving" @click="submitAssignCenterToCandidates">
                        {{ assignCandidateSaving ? 'സേവ് ചെയ്യുന്നു...' : 'സേവ് ചെയ്യുക' }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import UserService from "@/services/user.service";

export default {
    name: "QVPExamResult",
    data() {
        return {
            loading_list: false,
            list: [],
            count_total: 0,
            page_title: "",
            level: null,
            exam_id: "",
            exam_type: "QVP",
            exams: [],
            user_level: null,
            level_title: "",

            // District admin center management
            addCenterModalOpen: false,
            assignCentersModalOpen: false,
            districtsLoading: false,
            districts: [],
            selectedDistrictIdForCenters: "",
            selectedDistrictIdForPdf: "",
            zonesLoading: false,
            unitsLoading: false,
            centersLoading: false,
            centerSaving: false,
            assignSaving: false,
            pdfDownloading: false,
            deleteCenterLoadingId: null,

            editCenterModalOpen: false,
            editCenterSaving: false,
            editUnitsLoading: false,
            editZones: [],
            editUnits: [],
            editCenterId: null,
            editCenterForm: {
                district_id: "",
                center_name: "",
                zone_id: "",
                unit_id: "",
                convenor_name: "",
                convenor_phone: "",
                exam_time_from: "",
                exam_time_to: "",
            },
            zones: [],
            units: [],
            centers: [],
            selectedCenterIds: [],
            selectedCandidateIds: [],
            assignCandidateCenterModalOpen: false,
            selectedDistrictIdForCandidateAssignment: "",
            selectedCenterIdForCandidates: "",
            // When assigning for a single candidate, prefill center if already assigned
            prefillCenterIdForCandidates: null,
            prefillCenterNameForCandidates: "",
            assignCandidateSaving: false,
            unassignCandidateSaving: false,
            assignmentExamId: null,
            assignCandidateFlash: { type: "", message: "" },
            centerForm: {
                district_id: "",
                center_name: "",
                zone_id: "",
                unit_id: "",
                convenor_name: "",
                convenor_phone: "",
                exam_time_from: "",
                exam_time_to: "",
            },
        };
    },
    computed: {
        currentUser() {
            return this.$store && this.$store.state && this.$store.state.auth
                ? this.$store.state.auth.user
                : null;
        },
        userLevel() {
            const raw = this.currentUser?.user_level;
            if (raw == null) return 0;
            const n = parseInt(String(raw), 10);
            return Number.isNaN(n) ? 0 : n;
        },
        isDistrictAdmin() {
            return this.userLevel === 2;
        },
        isStateAdmin() {
            return this.userLevel === 1 || this.userLevel === 0;
        },
        isCenterAdmin() {
            return this.isDistrictAdmin || this.isStateAdmin;
        },
        effectiveDistrictIdForCenters() {
            if (this.isDistrictAdmin) return this.currentUser?.district_id || null;
            const n = parseInt(String(this.selectedDistrictIdForCenters || ""), 10);
            return Number.isNaN(n) ? null : n;
        },
        effectiveDistrictIdForCandidateAssignment() {
            if (this.isDistrictAdmin) return this.currentUser?.district_id || null;
            const n = parseInt(String(this.selectedDistrictIdForCandidateAssignment || ""), 10);
            return Number.isNaN(n) ? null : n;
        },
        centersForCandidateAssignment() {
            const list = this.centers || [];
            const assignedIds = (this.selectedCenterIds || []).map((x) => parseInt(String(x), 10)).filter((x) => !Number.isNaN(x));
            if (!assignedIds.length) return list;
            const set = new Set(assignedIds);
            const onlyAssigned = list.filter((c) => set.has(c.id));
            return onlyAssigned.length ? onlyAssigned : list;
        },
        // List sorted by total mark (high to low)
        sortedList() {
            if (!this.list || !this.list.length) return [];
            return [...this.list].sort((a, b) => this.getTotalMark(b) - this.getTotalMark(a));
        },
    },
    mounted() {
        if (!this.currentUser) {
            location.replace("/");
        } else {
            this.user_level = parseInt(this.$route.params.sub_level);
            this.updateLevelTitle();
            if (this.$root && this.$root.updateParent) {
                this.$root.updateParent("QVP പരീക്ഷ ഫലം");
            }
            this.fetchReport();
            if (this.isStateAdmin) {
                this.fetchDistricts();
            }
        }
    },
    watch: {
        "$route.params.sub_level"(val) {
            this.user_level = parseInt(val);
            this.updateLevelTitle();
        },
    },
    methods: {
        updateLevelTitle() {
            // Kept for potential future use (no level label in table now)
            if (this.user_level === 1) {
                this.level_title = "ജില്ല";
            } else if (this.user_level === 2) {
                this.level_title = "സോൺ";
            } else if (this.user_level === 3) {
                this.level_title = "യൂണിറ്റ്";
            } else {
                this.level_title = "നില";
            }
        },
        fetchReport() {
            this.loading_list = true;
            const data = {
                user_level: this.user_level,
                user_level_id: parseInt(this.$route.params.id) || 0,
                exam_type: this.exam_type,
            };
            if (this.exam_id) {
                data.exam_id = parseInt(this.exam_id);
            }
            // Backend should return: list (with name, mobile/phone, total_mark), count_total, exams[], page_title, level
            UserService.authPostRequest("get-qvp-exam-result-report", data).then(
                (response) => {
                    if (response.data.status === "success" && response.data.data) {
                        this.list = response.data.data.list || [];
                        this.count_total = response.data.data.count_total || 0;
                        this.page_title =
                            response.data.data.page_title || "QVP പരീക്ഷ ഫലം റിപ്പോർട്ട്";
                        this.level = response.data.data.level;
                        this.exams = response.data.data.exams || [];
                    } else {
                        this.list = [];
                        this.count_total = 0;
                        this.page_title = response.data.data
                            ? response.data.data.page_title || ""
                            : "";
                    }
                    this.loading_list = false;
                },
                (error) => {
                    this.loading_list = false;
                    this.list = [];
                    this.count_total = 0;
                    const msg =
                        (error.response &&
                            error.response.data &&
                            error.response.data.message) ||
                        error.message ||
                        "Error loading QVP exam result report";
                    if (this.$toast) this.$toast.open(msg);
                    else alert(msg);
                }
            );
        },
        toast(msg) {
            if (this.$toast) this.$toast.open(msg);
            else alert(msg);
        },
        isQualified(item) {
            return this.getTotalMark(item) >= 48;
        },
        validatePhone(phone) {
            const p = String(phone || "").replace(/\D/g, "");
            return p.length === 10;
        },
        async downloadCentersCandidatesPdf() {
            if (!this.isCenterAdmin) return;
            if (!this.exam_id) {
                this.toast("PDF എടുക്കാൻ ആദ്യം പരീക്ഷ തിരഞ്ഞെടുക്കുക");
                return;
            }
            const districtId = this.isDistrictAdmin
                ? (this.currentUser?.district_id || null)
                : (parseInt(String(this.selectedDistrictIdForPdf || ""), 10) || null);
            if (!districtId) {
                this.toast("District തിരഞ്ഞെടുക്കുക");
                return;
            }

            this.pdfDownloading = true;
            try {
                const payload = {
                    exam_id: parseInt(String(this.exam_id), 10),
                    district_id: parseInt(String(districtId), 10),
                };

                const response = await UserService.authPostRequest(
                    "qvp-exam-centers-candidates-pdf",
                    payload,
                    { responseType: "blob" }
                );

                const blob = response.data;
                const contentType = (response.headers && response.headers["content-type"]) || "";
                if (contentType.indexOf("application/json") !== -1) {
                    const text = await new Promise((res) => {
                        const r = new FileReader();
                        r.onload = () => res(r.result);
                        r.readAsText(blob);
                    });
                    const json = JSON.parse(text);
                    this.toast(json.message || "Download failed");
                    return;
                }

                const url = window.URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = "qvp-centers-candidates-" + districtId + "-" + new Date().toISOString().slice(0, 10) + ".pdf";
                a.click();
                URL.revokeObjectURL(url);
            } catch (err) {
                const msg =
                    (err.response && err.response.data && err.response.data.message) ||
                    err.message ||
                    "Failed to download PDF";
                this.toast(msg);
            } finally {
                this.pdfDownloading = false;
            }
        },
        async fetchDistricts() {
            if (!this.isStateAdmin) return;
            try {
                this.districtsLoading = true;
                const res = await UserService.getSubLevels("get-sub-levels", { sub_level_type: 2, id: 0 });
                if (res.data.status === "success") {
                    this.districts = res.data.data?.list || [];
                    if (!this.selectedDistrictIdForPdf && this.districts.length === 1) {
                        this.selectedDistrictIdForPdf = String(this.districts[0].id);
                    }
                } else {
                    this.districts = [];
                }
            } catch (e) {
                this.districts = [];
            } finally {
                this.districtsLoading = false;
            }
        },
        async fetchZonesForDistrict() {
            if (!this.isCenterAdmin) return;
            const districtId = this.isDistrictAdmin
                ? this.currentUser?.district_id
                : parseInt(String(this.centerForm.district_id || this.selectedDistrictIdForCenters || ""), 10);
            if (!districtId) return;
            try {
                this.zonesLoading = true;
                const data = { sub_level_type: 3, id: districtId };
                const response = await UserService.getSubLevels("get-sub-levels", data);
                if (response.data.status === "success") {
                    this.zones = response.data.data?.list || [];
                } else {
                    this.zones = [];
                    this.toast(response.data.message || "Zones load failed");
                }
            } catch (e) {
                this.zones = [];
                this.toast(e.response?.data?.message || "Zones load failed");
            } finally {
                this.zonesLoading = false;
            }
        },
        async fetchUnitsForZone(zoneId) {
            if (!zoneId) return;
            try {
                this.unitsLoading = true;
                const data = { sub_level_type: 4, id: parseInt(String(zoneId), 10) };
                const response = await UserService.getSubLevels("get-sub-levels", data);
                if (response.data.status === "success") {
                    this.units = response.data.data?.list || [];
                } else {
                    this.units = [];
                    this.toast(response.data.message || "Units load failed");
                }
            } catch (e) {
                this.units = [];
                this.toast(e.response?.data?.message || "Units load failed");
            } finally {
                this.unitsLoading = false;
            }
        },
        onZoneChange() {
            this.centerForm.unit_id = "";
            this.units = [];
            if (this.centerForm.zone_id) {
                this.fetchUnitsForZone(this.centerForm.zone_id);
            }
        },
        openAddCenterModal() {
            if (!this.isCenterAdmin) return;
            this.centerForm = {
                district_id: this.isDistrictAdmin ? (this.currentUser?.district_id || "") : "",
                center_name: "",
                zone_id: "",
                unit_id: "",
                convenor_name: "",
                convenor_phone: "",
                exam_time_from: "",
                exam_time_to: "",
            };
            this.units = [];
            this.addCenterModalOpen = true;
            if (this.isStateAdmin && (!this.districts || this.districts.length === 0)) {
                this.fetchDistricts();
            }
            if (this.isDistrictAdmin && (!this.zones || this.zones.length === 0)) {
                this.fetchZonesForDistrict();
            }
        },
        closeAddCenterModal() {
            this.addCenterModalOpen = false;
        },
        onDistrictChangeForCenterForm() {
            this.centerForm.zone_id = "";
            this.centerForm.unit_id = "";
            this.zones = [];
            this.units = [];
            if (this.centerForm.district_id) {
                this.fetchZonesForDistrict();
            }
        },
        async submitAddCenter() {
            if (!this.isCenterAdmin) {
                this.toast("ഈ നടപടി ജില്ലാ/സ്റ്റേറ്റ് അഡ്മിനുകൾക്ക് മാത്രം സാധ്യമാണ്");
                return;
            }
            if (this.isStateAdmin && !this.centerForm.district_id) {
                this.toast("District തിരഞ്ഞെടുക്കുക");
                return;
            }
            if (!this.centerForm.center_name?.trim()) {
                this.toast("Center name നൽകുക");
                return;
            }
            if (!this.centerForm.zone_id || !this.centerForm.unit_id) {
                this.toast("Zone, Unit തിരഞ്ഞെടുക്കുക");
                return;
            }
            if (!this.centerForm.convenor_name?.trim() || !this.centerForm.convenor_phone) {
                this.toast("Convenor details നൽകുക");
                return;
            }
            if (!this.validatePhone(this.centerForm.convenor_phone)) {
                this.toast("സാധുവായ 10 അക്ക ഫോൺ നമ്പർ നൽകുക");
                return;
            }
            if (!this.centerForm.exam_time_from || !this.centerForm.exam_time_to) {
                this.toast("Exam time നൽകുക");
                return;
            }
            try {
                this.centerSaving = true;
                const payload = {
                    center_name: this.centerForm.center_name.trim(),
                    zone_id: parseInt(String(this.centerForm.zone_id), 10),
                    unit_id: parseInt(String(this.centerForm.unit_id), 10),
                    convenor_name: this.centerForm.convenor_name.trim(),
                    convenor_phone: String(this.centerForm.convenor_phone).replace(/\D/g, ""),
                    exam_time_from: this.centerForm.exam_time_from,
                    exam_time_to: this.centerForm.exam_time_to,
                    district_id: this.isDistrictAdmin ? (this.currentUser?.district_id || null) : parseInt(String(this.centerForm.district_id), 10),
                };
                const response = await UserService.authPostRequest("qvp-exam-center/store", payload);
                if (response.data.status === "success") {
                    this.toast(response.data.message || "Saved");
                    this.closeAddCenterModal();
                } else {
                    this.toast(response.data.message || "Save failed");
                }
            } catch (e) {
                this.toast(e.response?.data?.message || "Save failed");
            } finally {
                this.centerSaving = false;
            }
        },
        async fetchCentersForDistrict() {
            if (!this.isCenterAdmin) return;
            const districtId = this.effectiveDistrictIdForCenters;
            if (!districtId) return;
            try {
                this.centersLoading = true;
                const response = await UserService.authGetRequest("qvp-exam-centers", { district_id: districtId });
                if (response.data.status === "success") {
                    this.centers = response.data.data?.list || response.data.data || [];
                    const pre = response.data.data?.assigned_center_ids;
                    if (Array.isArray(pre)) {
                        this.selectedCenterIds = pre.map((x) => (typeof x === "number" ? x : parseInt(String(x), 10))).filter((x) => !Number.isNaN(x));
                    } else {
                        // fallback: if API returns `assigned` flag per center
                        this.selectedCenterIds = (this.centers || [])
                            .filter((c) => c.assigned === true || c.is_assigned === true)
                            .map((c) => c.id);
                    }

                    // If we are assigning for a single candidate and they already have a center,
                    // preselect it once centers are loaded.
                    if (this.prefillCenterIdForCandidates) {
                        const pid = parseInt(String(this.prefillCenterIdForCandidates), 10);
                        const exists = (this.centers || []).some((c) => parseInt(String(c.id), 10) === pid);
                        if (exists) {
                            this.selectedCenterIdForCandidates = pid;
                        }
                    }
                } else {
                    this.centers = [];
                    this.selectedCenterIds = [];
                    this.toast(response.data.message || "Centers load failed");
                }
            } catch (e) {
                this.centers = [];
                this.selectedCenterIds = [];
                this.toast(e.response?.data?.message || "Centers load failed");
            } finally {
                this.centersLoading = false;
            }
        },
        openAssignCentersModal() {
            if (!this.isCenterAdmin) return;
            this.assignCentersModalOpen = true;
            if (this.isStateAdmin && (!this.districts || this.districts.length === 0)) {
                this.fetchDistricts();
            }
            if (this.isDistrictAdmin) {
                this.selectedDistrictIdForCenters = String(this.currentUser?.district_id || "");
                this.fetchCentersForDistrict();
            }
        },
        closeAssignCentersModal() {
            this.assignCentersModalOpen = false;
        },
        onDistrictChangeForCenters() {
            this.selectedCenterIds = [];
            this.centers = [];
            if (this.selectedDistrictIdForCenters) {
                this.fetchCentersForDistrict();
            }
        },
        async submitAssignCenters() {
            if (!this.isCenterAdmin) return;
            const districtId = this.effectiveDistrictIdForCenters;
            if (!districtId) {
                this.toast("District ID ഇല്ല");
                return;
            }
            try {
                this.assignSaving = true;
                const payload = {
                    district_id: parseInt(String(districtId), 10),
                    center_ids: (this.selectedCenterIds || []).map((x) => (typeof x === "number" ? x : parseInt(String(x), 10))).filter((x) => !Number.isNaN(x)),
                };
                const response = await UserService.authPostRequest("assign-qvp-exam-centers", payload);
                if (response.data.status === "success") {
                    this.toast(response.data.message || "Saved");
                    this.closeAssignCentersModal();
                } else {
                    this.toast(response.data.message || "Save failed");
                }
            } catch (e) {
                this.toast(e.response?.data?.message || "Save failed");
            } finally {
                this.assignSaving = false;
            }
        },
        async confirmDeleteCenter(center) {
            if (!this.isCenterAdmin) return;
            const ok = confirm("ഈ കേന്ദ്രം ഡിലീറ്റ് ചെയ്യണോ? (Assigned candidates ലും remove ചെയ്യും)");
            if (!ok) return;

            try {
                this.deleteCenterLoadingId = center?.id || null;
                await UserService.authPostRequest(`qvp-exam-center-delete/${center.id}`, {});
                // Refresh centers list for current district
                await this.fetchCentersForDistrict();
            } catch (e) {
                this.toast(e.response?.data?.message || "Delete failed");
            } finally {
                this.deleteCenterLoadingId = null;
            }
        },
        async openEditCenterModal(center) {
            if (!this.isCenterAdmin) return;
            this.editCenterId = center?.id || null;
            this.editCenterForm = {
                district_id: this.isDistrictAdmin
                    ? String(this.currentUser?.district_id || center?.district_id || "")
                    : String(center?.district_id || this.selectedDistrictIdForCenters || ""),
                center_name: center?.center_name || "",
                zone_id: String(center?.zone_id || ""),
                unit_id: String(center?.unit_id || ""),
                convenor_name: center?.convenor_name || "",
                convenor_phone: String(center?.convenor_phone || ""),
                exam_time_from: center?.exam_time_from || "",
                exam_time_to: center?.exam_time_to || "",
            };
            this.editZones = [];
            this.editUnits = [];
            this.editCenterModalOpen = true;

            if (this.isStateAdmin && (!this.districts || this.districts.length === 0)) {
                await this.fetchDistricts();
            }
            await this.fetchZonesForEditForm();
            if (this.editCenterForm.zone_id) {
                await this.fetchUnitsForEditZone(this.editCenterForm.zone_id);
            }
        },
        closeEditCenterModal() {
            this.editCenterModalOpen = false;
            this.editCenterSaving = false;
            this.editUnitsLoading = false;
            this.editZones = [];
            this.editUnits = [];
            this.editCenterId = null;
        },
        onDistrictChangeForEditForm() {
            this.editCenterForm.zone_id = "";
            this.editCenterForm.unit_id = "";
            this.editZones = [];
            this.editUnits = [];
            this.fetchZonesForEditForm();
        },
        onZoneChangeForEditForm() {
            this.editCenterForm.unit_id = "";
            this.editUnits = [];
            if (this.editCenterForm.zone_id) {
                this.fetchUnitsForEditZone(this.editCenterForm.zone_id);
            }
        },
        async fetchZonesForEditForm() {
            const districtId = this.isDistrictAdmin
                ? (this.currentUser?.district_id || null)
                : (parseInt(String(this.editCenterForm.district_id || ""), 10) || null);
            if (!districtId) return;
            try {
                this.zonesLoading = true;
                const res = await UserService.getSubLevels("get-sub-levels", { sub_level_type: 3, id: districtId });
                this.editZones = res.data?.data?.list || [];
            } catch (e) {
                this.editZones = [];
            } finally {
                this.zonesLoading = false;
            }
        },
        async fetchUnitsForEditZone(zoneId) {
            if (!zoneId) return;
            try {
                this.editUnitsLoading = true;
                const res = await UserService.getSubLevels("get-sub-levels", { sub_level_type: 4, id: parseInt(String(zoneId), 10) });
                this.editUnits = res.data?.data?.list || [];
            } catch (e) {
                this.editUnits = [];
            } finally {
                this.editUnitsLoading = false;
            }
        },
        async submitEditCenter() {
            if (!this.isCenterAdmin || !this.editCenterId) return;
            if (this.isStateAdmin && !this.editCenterForm.district_id) {
                this.toast("District തിരഞ്ഞെടുക്കുക");
                return;
            }
            if (!this.editCenterForm.center_name?.trim()) {
                this.toast("Center name നൽകുക");
                return;
            }
            if (!this.editCenterForm.zone_id || !this.editCenterForm.unit_id) {
                this.toast("Zone, Unit തിരഞ്ഞെടുക്കുക");
                return;
            }
            if (!this.editCenterForm.convenor_name?.trim() || !this.editCenterForm.convenor_phone) {
                this.toast("Convenor details നൽകുക");
                return;
            }
            if (!this.validatePhone(this.editCenterForm.convenor_phone)) {
                this.toast("സാധുവായ 10 അക്ക ഫോൺ നമ്പർ നൽകുക");
                return;
            }
            if (!this.editCenterForm.exam_time_from || !this.editCenterForm.exam_time_to) {
                this.toast("Exam time നൽകുക");
                return;
            }

            try {
                this.editCenterSaving = true;
                const payload = {
                    center_name: this.editCenterForm.center_name.trim(),
                    zone_id: parseInt(String(this.editCenterForm.zone_id), 10),
                    unit_id: parseInt(String(this.editCenterForm.unit_id), 10),
                    convenor_name: this.editCenterForm.convenor_name.trim(),
                    convenor_phone: String(this.editCenterForm.convenor_phone).replace(/\D/g, ""),
                    exam_time_from: this.editCenterForm.exam_time_from,
                    exam_time_to: this.editCenterForm.exam_time_to,
                    district_id: this.isDistrictAdmin ? (this.currentUser?.district_id || null) : parseInt(String(this.editCenterForm.district_id), 10),
                };
                const res = await UserService.authPostRequest(`qvp-exam-center/${this.editCenterId}`, payload);
                if (res.data.status === "success") {
                    this.toast(res.data.message || "Updated");
                    await this.fetchCentersForDistrict();
                    this.closeEditCenterModal();
                } else {
                    this.toast(res.data.message || "Update failed");
                }
            } catch (e) {
                this.toast(e.response?.data?.message || "Update failed");
            } finally {
                this.editCenterSaving = false;
            }
        },
        openAssignForSingle(item) {
            if (!this.isCenterAdmin) return;
            this.selectedCandidateIds = [item.id];
            if (this.isStateAdmin) {
                this.selectedDistrictIdForCandidateAssignment = item.district_id ? String(item.district_id) : "";
            }
            // Allow assign even when exam filter is "All" by using row's exam_id
            this.assignmentExamId = item.exam_id ? parseInt(String(item.exam_id), 10) : null;
            this.prefillCenterIdForCandidates = item.assigned_center_id ? parseInt(String(item.assigned_center_id), 10) : null;
            this.prefillCenterNameForCandidates = item.assigned_center_name || "";
            this.openAssignCandidateCenterModal();
        },
        openAssignCandidateCenterModal() {
            if (!this.isCenterAdmin) return;
            // For bulk selection (checkboxes), require exam filter; for row assign, we use assignmentExamId
            if (!this.exam_id && !this.assignmentExamId) {
                this.toast("Assign ചെയ്യാൻ ആദ്യം പരീക്ഷ തിരഞ്ഞെടുക്കുക");
                return;
            }
            if (!this.selectedCandidateIds.length) {
                this.toast("Candidates select ചെയ്യുക");
                return;
            }
            if (this.isStateAdmin) {
                if (!this.districts || this.districts.length === 0) this.fetchDistricts();
                // Auto-detect district from selected candidates (must be single district)
                const selectedIdSet = new Set(
                    (this.selectedCandidateIds || [])
                        .map((x) => parseInt(String(x), 10))
                        .filter((x) => !Number.isNaN(x))
                );
                const selected = (this.list || []).filter((x) => selectedIdSet.has(parseInt(String(x.id), 10)));
                const districtIds = selected.map((x) => x.district_id).filter((x) => !!x);
                const unique = Array.from(new Set(districtIds.map((x) => parseInt(String(x), 10)).filter((x) => !Number.isNaN(x))));
                if (unique.length === 1 && !this.selectedDistrictIdForCandidateAssignment) {
                    this.selectedDistrictIdForCandidateAssignment = String(unique[0]);
                }
                if (unique.length > 1) {
                    this.toast("Different districts candidates ഒരുമിച്ച് assign ചെയ്യാൻ സാധിക്കില്ല");
                    return;
                }
                if (!this.selectedDistrictIdForCandidateAssignment) {
                    this.toast("District തിരഞ്ഞെടുക്കുക");
                    return;
                }
            }
            this.assignCandidateCenterModalOpen = true;
            // Keep prefilled value for single-candidate flow; otherwise clear.
            if (!this.prefillCenterIdForCandidates) this.selectedCenterIdForCandidates = "";
            // For candidate assignment we want the "assigned centers" list for that district
            this.selectedDistrictIdForCenters = this.isDistrictAdmin
                ? String(this.currentUser?.district_id || "")
                : String(this.selectedDistrictIdForCandidateAssignment || "");
            this.fetchCentersForDistrict();
        },
        closeAssignCandidateCenterModal() {
            this.assignCandidateCenterModalOpen = false;
            this.selectedCenterIdForCandidates = "";
            this.selectedDistrictIdForCandidateAssignment = "";
            this.assignmentExamId = null;
            this.prefillCenterIdForCandidates = null;
            this.prefillCenterNameForCandidates = "";
            this.assignCandidateFlash = { type: "", message: "" };
        },
        onDistrictChangeForCandidateAssignment() {
            this.selectedCenterIdForCandidates = "";
            this.selectedDistrictIdForCenters = String(this.selectedDistrictIdForCandidateAssignment || "");
            this.fetchCentersForDistrict();
        },
        async submitAssignCenterToCandidates() {
            if (!this.isCenterAdmin) return;
            const districtId = this.effectiveDistrictIdForCandidateAssignment;
            if (!districtId) {
                this.toast("District ID ഇല്ല");
                this.assignCandidateFlash = { type: "error", message: "District ID ഇല്ല" };
                return;
            }
            const examIdToUse = this.exam_id ? parseInt(String(this.exam_id), 10) : (this.assignmentExamId || null);
            if (!examIdToUse) {
                this.toast("Assign ചെയ്യാൻ ആദ്യം പരീക്ഷ തിരഞ്ഞെടുക്കുക");
                this.assignCandidateFlash = { type: "error", message: "Assign ചെയ്യാൻ ആദ്യം പരീക്ഷ തിരഞ്ഞെടുക്കുക" };
                return;
            }
            if (!this.selectedCenterIdForCandidates) {
                this.toast("Center തിരഞ്ഞെടുക്കുക");
                this.assignCandidateFlash = { type: "error", message: "Center തിരഞ്ഞെടുക്കുക" };
                return;
            }
            const candidateIds = (this.selectedCandidateIds || []).map((x) => parseInt(String(x), 10)).filter((x) => !Number.isNaN(x));
            if (!candidateIds.length) {
                this.toast("Candidates select ചെയ്യുക");
                this.assignCandidateFlash = { type: "error", message: "Candidates select ചെയ്യുക" };
                return;
            }
            try {
                this.assignCandidateSaving = true;
                this.assignCandidateFlash = { type: "", message: "" };
                const payload = {
                    district_id: parseInt(String(districtId), 10),
                    exam_id: examIdToUse,
                    center_id: parseInt(String(this.selectedCenterIdForCandidates), 10),
                    candidate_ids: candidateIds,
                };
                const response = await UserService.authPostRequest("assign-qvp-exam-center-candidates", payload);
                if (response.data.status === "success") {
                    const msg = response.data.message || "വിജയകരമായി സേവ് ചെയ്തു";
                    this.toast(msg);
                    this.assignCandidateFlash = { type: "success", message: msg };
                    // Keep modal visible briefly so user sees confirmation
                    setTimeout(() => {
                        this.closeAssignCandidateCenterModal();
                        this.fetchReport();
                    }, 600);
                } else {
                    const msg = response.data.message || "Save failed";
                    this.toast(msg);
                    this.assignCandidateFlash = { type: "error", message: msg };
                }
            } catch (e) {
                const msg = e.response?.data?.message || "Save failed";
                this.toast(msg);
                this.assignCandidateFlash = { type: "error", message: msg };
            } finally {
                this.assignCandidateSaving = false;
            }
        },
        async submitUnassignCenterForCandidates() {
            if (!this.isCenterAdmin) return;
            const districtId = this.effectiveDistrictIdForCandidateAssignment;
            if (!districtId) {
                this.toast("District ID ഇല്ല");
                this.assignCandidateFlash = { type: "error", message: "District ID ഇല്ല" };
                return;
            }
            const examIdToUse = this.exam_id
                ? parseInt(String(this.exam_id), 10)
                : (this.assignmentExamId || null);
            if (!examIdToUse) {
                this.toast("Clear ചെയ്യാൻ ആദ്യം പരീക്ഷ തിരഞ്ഞെടുക്കുക");
                this.assignCandidateFlash = { type: "error", message: "Clear ചെയ്യാൻ ആദ്യം പരീക്ഷ തിരഞ്ഞെടുക്കുക" };
                return;
            }
            const candidateIds = (this.selectedCandidateIds || [])
                .map((x) => parseInt(String(x), 10))
                .filter((x) => !Number.isNaN(x));
            if (!candidateIds.length) {
                this.toast("Candidates select ചെയ്യുക");
                this.assignCandidateFlash = { type: "error", message: "Candidates select ചെയ്യുക" };
                return;
            }
            const ok = confirm("Selected candidates center assignment clear ചെയ്യണോ?");
            if (!ok) return;

            try {
                this.unassignCandidateSaving = true;
                this.assignCandidateFlash = { type: "", message: "" };
                const payload = {
                    district_id: parseInt(String(districtId), 10),
                    exam_id: examIdToUse,
                    candidate_ids: candidateIds,
                };
                const response = await UserService.authPostRequest("unassign-qvp-exam-center-candidates", payload);
                if (response.data.status === "success") {
                    const msg = response.data.message || "Cleared";
                    this.toast(msg);
                    this.assignCandidateFlash = { type: "success", message: msg };
                    setTimeout(() => {
                        this.closeAssignCandidateCenterModal();
                        this.fetchReport();
                    }, 600);
                } else {
                    const msg = response.data.message || "Clear failed";
                    this.toast(msg);
                    this.assignCandidateFlash = { type: "error", message: msg };
                }
            } catch (e) {
                const msg = e.response?.data?.message || "Clear failed";
                this.toast(msg);
                this.assignCandidateFlash = { type: "error", message: msg };
            } finally {
                this.unassignCandidateSaving = false;
            }
        },
        // Safely get mobile/phone number
        getNumber(item) {
            return item.mobile || item.phone || item.contact || "";
        },
        // Safely get total mark as number
        getTotalMark(item) {
            const raw =
                item.total_mark ??
                item.mark ??
                item.total_score ??
                item.score ??
                0;
            const num = Number(raw);
            return isNaN(num) ? 0 : num;
        },
    },
};
</script>

<style scoped>
.stat-box {
    padding: 14px 16px;
    min-height: 72px;
}
.stat-box .title {
    font-size: 15px;
    margin-bottom: 6px;
    color: #374151;
}
.stat-box .value {
    font-size: 24px;
    font-weight: 700;
    letter-spacing: -0.02em;
    line-height: 1.2em;
    color: #27173E;
}
.label {
    display: block;
    margin-bottom: 4px;
    font-size: 13px;
    font-weight: 600;
    color: #1a418c;
}

.add-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 12px;
}
.add-btn ion-icon {
    font-size: 1.25rem;
}
.add-btn .center-icon {
    font-size: 1.15rem;
    opacity: 0.95;
}
.kebab-btn {
    width: 44px;
    height: 44px;
    border-radius: 10px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}
.kebab-btn ion-icon {
    font-size: 1.25rem;
}
.kebab-fallback {
    display: inline-block;
    font-size: 1.4rem;
    line-height: 1;
    color: inherit;
}

.modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.4);
    display: flex;
    align-items: flex-end;
    justify-content: center;
    z-index: 9999;
    padding: 0;
}
.modal-overlay.action-sheet-open {
    align-items: flex-end;
}
.modal-sheet {
    background: #fff;
    border-radius: 16px 16px 0 0;
    padding: 20px;
    width: 100%;
    max-width: 480px;
    max-height: 85vh;
    overflow-y: auto;
    animation: slideUp 0.3s ease;
}
.modal-sheet-handle {
    width: 36px;
    height: 4px;
    background: #dee2e6;
    border-radius: 2px;
    margin: 0 auto 16px;
}
.modal-sheet .modal-title {
    font-size: 1rem;
    font-weight: 600;
    margin-bottom: 16px;
    color: #1a418c;
}
.form-group {
    margin-bottom: 14px;
}
.form-label {
    display: block;
    font-weight: 500;
    margin-bottom: 6px;
    color: #333;
}
.modal-actions {
    display: flex;
    gap: 10px;
    justify-content: flex-end;
    margin-top: 20px;
}
.centers-checklist {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 6px 0;
}
.center-check-item {
    display: block;
    background: #f8f9fa;
    border-radius: 10px;
    padding: 12px;
}
.center-check-item input {
    margin-right: 10px;
}
.assign-flash {
    border-radius: 10px;
    padding: 10px 12px;
    margin-bottom: 12px;
    font-size: 14px;
    line-height: 1.4;
}
.flash-success {
    background: #ecfdf5;
    border: 1px solid #a7f3d0;
    color: #065f46;
}
.flash-error {
    background: #fef2f2;
    border: 1px solid #fecaca;
    color: #991b1b;
}
@keyframes slideUp {
    from {
        transform: translateY(100%);
    }
    to {
        transform: translateY(0);
    }
}
</style>

